import { cn } from '@/lib/utils'
import {
    DayCellContentArg,
    DayHeaderContentArg,
    EventContentArg,
    MoreLinkArg,
} from '@fullcalendar/core/index.js'
import dayGridPlugin from '@fullcalendar/daygrid'
import FullCalendar from '@fullcalendar/react'
import dayjs from 'dayjs'
import { XIcon } from 'lucide-react'
import React, { useRef } from 'react'
import { match } from 'ts-pattern'
import Dialog from '../guides/Dialog'
import IconButton from './Button/IconButton'

/**
 * Adiciona plugins, locale, toolbar e customiza os conteúdos das células
 *
 * @returns Componente React FullCalendar customizado
 *
 * @example
 * ```tsx
 * const ref = useCalendarRef() // opcional, usado para acessar a api do calendário
 *
 * <CalendarioEventos
 *   ref={ref}
 *   events={[...]}
 * />
 * ```
 */
const CalendarioEventos = React.forwardRef<
    FullCalendar,
    React.ComponentProps<typeof FullCalendar>
>(({ moreLinkClick, ...props }, ref) => {
    const [popoverProps, setPopoverProps] =
        React.useState<OptionalLinkArg>(null)

    return [
        <FullCalendar
            key="full-calendar"
            ref={ref}
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            dayMaxEvents={1}
            locale={'pt-br'}
            headerToolbar={false}
            defaultAllDay={true}
            timeZone="UTC"
            dayCellContent={dayCellContent}
            dayHeaderContent={dayHeaderContent}
            eventContent={eventContent}
            moreLinkClick={
                moreLinkClick
                    ? moreLinkClick
                    : info => {
                          info.jsEvent.preventDefault()
                          info.jsEvent.stopPropagation()
                          setPopoverProps(info)
                      }
            }
            {...props}
        />,
        <PopOver
            key="popover"
            onClose={() => setPopoverProps(null)}
            open={!!popoverProps}
            {...popoverProps}
        />,
    ]
})

type OptionalLinkArg = Partial<MoreLinkArg> | null
type PopOverProps = {
    open: boolean
    onClose: () => void
} & OptionalLinkArg

function PopOver({ open, onClose, ...info }: PopOverProps) {
    if (!info || !info.hiddenSegs) return null
    return (
        <Dialog.Root open={open} onClose={onClose} width="fit" height="fit">
            <Dialog.Title className="p-2 min-h-fit">
                {dayjs.utc(info.date).format('LL')}
                <div className="flex flex-grow" />
                <IconButton onClick={onClose} title="Fechar">
                    <XIcon />
                </IconButton>
            </Dialog.Title>
            <Dialog.Content dividers className="h-fit p-0">
                {info.hiddenSegs.map((seg, index) => (
                    <div
                        key={index}
                        className="mb-2 border p-2 rounded min-w-full"
                        style={{
                            backgroundColor: seg.event.backgroundColor,
                            color: seg.event.textColor,
                            borderColor: seg.event.borderColor,
                        }}
                    >
                        {seg.event.title}
                    </div>
                ))}
            </Dialog.Content>
        </Dialog.Root>
    )
}

CalendarioEventos.displayName = 'CalendarioEventos'

export default CalendarioEventos

function dayCellContent(info: DayCellContentArg) {
    const date = dayjs.utc(info.date)
    const color = match(date)
        .when(
            d => d.day() === 0,
            () => 'text-red-600' as const,
        )
        .when(
            d => d.day() === 6,
            () => 'text-red-400' as const,
        )
        .otherwise(() => 'text-inherit' as const)

    return (
        <span
            key={date.toISOString()}
            className={cn('text-center text-sm font-semibold mr-2', color)}
        >
            {date.format('D')}
        </span>
    )
}

function dayHeaderContent(info: DayHeaderContentArg) {
    const date = dayjs.utc(info.date)
    const color = match(date)
        .when(
            d => d.day() === 0,
            () => 'text-red-600',
        )
        .when(
            d => d.day() === 6,
            () => 'text-red-400',
        )
        .otherwise(() => 'text-inherit' as const)

    return (
        <span
            className={cn('text-center text-sm font-semibold', color)}
            key={date.toISOString()}
        >
            {date.format('ddd')}
        </span>
    )
}

function eventContent(eventInfo: EventContentArg) {
    return (
        <div
            className={cn(
                'text-sm truncate px-2 min-w-full text-center bg-primary text-primary-foreground',
            )}
            title={eventInfo.event.title}
        >
            {eventInfo.event.title}
        </div>
    )
}

/**
 * Hook para criar uma ref para o FullCalendar
 */
export function useCalendarRef() {
    return useRef<FullCalendar>(null)
}
